// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTableRowExpansion.tsx
 * @input React, StyleX, Icon, Table types, i18n (useTranslator)
 * @output Exports useTableRowExpansion hook + config type
 * @position Row-expansion plugin (detail panel); consumed by Table via plugins prop
 *
 * Expands a full-width detail panel below a row, rendered by the consumer's
 * `renderExpanded(item)`. For hierarchical/tree tables (child rows that reuse
 * the parent columns) use `useTableTreeData` + `useTableTreeState` instead.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Table/index.ts (exports)
 * - /packages/core/src/Table/useTableRowExpansion.doc.mjs
 */
import { useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../../../theme/tokens.stylex.js";
import { spacingVars, colorVars, radiusVars } from "../../../theme/tokens.stylex.js";
import { Icon } from "../../../Icon/index.js";
import { resolveContextActions } from "../../tableContextMenu.js";
import { useTranslator } from "../../../i18n/index.js";

// =============================================================================
// Config
// =============================================================================

/**
 * Configuration for useTableRowExpansion (detail-panel mode).
 *
 * The consumer owns expansion state; the plugin provides the chevron UI, the
 * full-width detail panel rendered below an expanded row, and a right-click
 * "Expand/Collapse row" action.
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const EXPANSION_COLUMN_WIDTH = {
  type: 'pixel',
  value: 40
};
// =============================================================================
// Chevron Cell
// =============================================================================

function ExpansionChevron({
  isExpanded,
  onToggle
}) {
  const t = useTranslator();
  return /*#__PURE__*/_jsx("button", {
    type: "button",
    ...{
      0: {
        className: "x3nfvp2 x6s0dn4 xl56j7k x1mtvzj0 xvpftlf xx3sua9 x1ypdohk xv9yike x12pfavo xx6bhzk x1717udv x1ilzqfv x1cqbx0l"
      },
      1: {
        className: "x3nfvp2 x6s0dn4 xl56j7k x1mtvzj0 xvpftlf xx3sua9 x1ypdohk xv9yike x12pfavo xx6bhzk x1717udv x1ilzqfv x1cqbx0l x1iffjtl"
      }
    }[!!isExpanded << 0],
    onClick: e => {
      e.stopPropagation();
      onToggle();
    },
    "aria-label": isExpanded ? t('@astryx.tableRowExpansion.collapseRow') : t('@astryx.tableRowExpansion.expandRow'),
    "aria-expanded": isExpanded,
    children: /*#__PURE__*/_jsx(Icon, {
      icon: "chevronRight",
      size: "xsm"
    })
  });
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Returns a TablePlugin that expands a full-width detail panel below a row.
 *
 * The consumer owns the `expandedKeys` set (via `useState`); the plugin adds
 * a leading chevron column, a right-click expand/collapse action, and renders
 * `renderExpanded(item)` in a full-width panel below each expanded row.
 *
 * For hierarchical data (child rows sharing the parent columns) use
 * `useTableTreeData` + `useTableTreeState` instead.
 *
 * @example
 * ```
 * const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
 * const expansion = useTableRowExpansion({
 *   expandedKeys,
 *   onToggle: key =>
 *     setExpandedKeys(prev => {
 *       const next = new Set(prev);
 *       next.has(key) ? next.delete(key) : next.add(key);
 *       return next;
 *     }),
 *   getRowKey: item => item.id,
 *   renderExpanded: item => <OrderDetails order={item} />,
 * });
 * <Table data={data} columns={columns} idKey="id" plugins={{expansion}} />;
 * ```
 */
export function useTableRowExpansion(config) {
  const {
    expandedKeys,
    onToggle,
    getRowKey,
    renderExpanded,
    getIsItemExpandable
  } = config;
  const t = useTranslator();

  // Final rendered column count, captured in transformColumns (pipeline step
  // 1) and read in transformBodyRow for the detail panel's colSpan.
  const columnCountRef = useRef(1);
  const expansionColumn = useMemo(() => ({
    key: '__expansion',
    header: '',
    width: EXPANSION_COLUMN_WIDTH,
    resizable: false,
    renderCell: item => {
      const expandable = getIsItemExpandable ? getIsItemExpandable(item) : true;
      if (!expandable) {
        return null;
      }
      const key = getRowKey(item);
      return /*#__PURE__*/_jsx(ExpansionChevron, {
        isExpanded: expandedKeys.has(key),
        onToggle: () => onToggle(key)
      });
    }
  }), [expandedKeys, onToggle, getRowKey, getIsItemExpandable]);
  return useMemo(() => ({
    transformColumns(columns) {
      const withExpansion = [expansionColumn, ...columns];
      columnCountRef.current = withExpansion.length;
      return withExpansion;
    },
    transformBodyCell(props, _column, item) {
      // Contribute the expand/collapse action on every cell; BaseTable
      // aggregates them into one menu per row. Skip non-expandable rows.
      const expandable = getIsItemExpandable ? getIsItemExpandable(item) : true;
      if (!expandable) {
        return props;
      }
      const key = getRowKey(item);
      const isExpanded = expandedKeys.has(key);
      return {
        ...props,
        contextMenuActions: () => [...resolveContextActions(props.contextMenuActions), {
          id: 'row-expansion-toggle',
          group: 'row-expansion',
          label: isExpanded ? t('@astryx.tableRowExpansion.collapseRow') : t('@astryx.tableRowExpansion.expandRow'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: isExpanded ? 'chevronDown' : 'chevronRight',
            size: "xsm",
            "aria-hidden": true
          }),
          onSelect: () => onToggle(key)
        }]
      };
    },
    transformBodyRow(props, item) {
      const expandable = getIsItemExpandable ? getIsItemExpandable(item) : true;
      if (!expandable) {
        return props;
      }
      const key = getRowKey(item);
      if (!expandedKeys.has(key)) {
        return props;
      }
      const panel = /*#__PURE__*/_jsx("tr", {
        ...{
          className: "xwmxj5m"
        },
        children: /*#__PURE__*/_jsx("td", {
          colSpan: columnCountRef.current,
          ...{
            className: "x1na6nto xzz68wx"
          },
          children: renderExpanded(item)
        })
      }, `${key}-expanded`);
      return {
        ...props,
        afterRow: props.afterRow ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [props.afterRow, panel]
        }) : panel
      };
    }
  }), [expandedKeys, getRowKey, renderExpanded, getIsItemExpandable, onToggle, t, expansionColumn]);
}